/**
 * Demo data for the Insurance screen (Operations group).
 *
 * The dev3 database has 0 rows in both InsuranceProductData and
 * UserInsuranceData, and no seeder creates any -- so the screen, which is
 * otherwise complete, could only ever render its empty state. This file gives
 * it something to show without touching the API or the database.
 *
 * The leads are built from the SAME people as the rest of the demo panel
 * (`users` in dataset.js), so a customer who appears here also appears under
 * User Management, instead of the panel carrying two unrelated sets of names.
 *
 * Everything is generated from a fixed seed and anchored to dataset.js's fixed
 * TODAY, so the list is byte-identical on every reload -- a filter that hides a
 * row hides the same row tomorrow.
 *
 * Its own RNG stream (a different seed) rather than dataset.js's shared one:
 * drawing from that stream after the fact would shift every number the other
 * screens depend on.
 */
import { users, TODAY, makeRng } from "./dataset";
import { inRange, matchesAny, paginate, textMatch } from "./queries";
import { PAGE_LENGTH } from "../config";

const rng = makeRng(20260820);
const int = (min, max) => Math.floor(rng() * (max - min + 1)) + min;

function pickWeighted(pairs) {
  const total = pairs.reduce((s, p) => s + p[1], 0);
  let r = rng() * total;
  for (const [value, weight] of pairs) {
    r -= weight;
    if (r <= 0) return value;
  }
  return pairs[pairs.length - 1][0];
}

function daysAgo(n) {
  const d = new Date(TODAY.getTime());
  d.setDate(d.getDate() - n);
  d.setHours(int(9, 19), int(0, 59), 0, 0);
  return d;
}

/** "2026-08-20" -- local calendar date, which is what the filters compare. */
function ymd(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** "2026-08-20 14:32" -- the shape the server's serializer sends. */
function stamp(d) {
  const p = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}`
  );
}

/* Statuses. "Rejected" is not in the model's choices but the panel writes it
   and the server's bucket strip counts it, so it is a real fourth state. */
export const INSURANCE_STATUS = {
  INTERESTED: "Interested",
  CONVERTED: "Converted",
  REJECTED: "Rejected",
};

/* ------------------------------------------------------------- products */

/**
 * The product dropdown renders `title`, not `name` (item-text="title" on the
 * page), so a product without a title shows as a blank row. Both are set.
 *
 * `utm_link` carries the {LeadID} placeholder the real InsuranceProductClick
 * endpoint fills in with a zero-padded row id -- reproduced below so the
 * "Copy UTM Link" button copies something that looks like the real thing.
 */
const PRODUCT_SEED = [
  ["Term Life Cover", "Term Life Insurance", "https://karrofin.example/insurance/term-life", 1],
  ["Health Shield", "Health Insurance (Family Floater)", "https://karrofin.example/insurance/health-shield", 2],
  ["Two-Wheeler Secure", "Two-Wheeler Insurance", "https://karrofin.example/insurance/two-wheeler", 3],
  ["Personal Accident Plan", "Personal Accident Cover", "https://karrofin.example/insurance/personal-accident", 4],
  ["Home Protect", "Home Insurance", "https://karrofin.example/insurance/home-protect", 5],
  ["Travel Assure", "Travel Insurance", "https://karrofin.example/insurance/travel-assure", 6],
];

export const products = PRODUCT_SEED.map(([name, title, web_url, priority], i) => {
  const created = daysAgo(200 - i * 12);
  return {
    id: 1 + i,
    name,
    title,
    logo: null,
    web_url,
    utm_link: `${web_url}?utm_source=karrofin&utm_medium=app&lead_id={LeadID}`,
    priority,
    // One inactive product, so "Any product" listing only the active five is
    // visible behaviour rather than something you have to take on trust.
    is_active: name !== "Home Protect",
    created: stamp(created),
    modified: stamp(created),
  };
});

/* ---------------------------------------------------------------- leads */

const LEAD_COUNT = 52;

/**
 * Weighted so no bucket in the strip is ever empty -- an empty bucket reads as
 * a broken screen rather than as a filter that found nothing.
 */
const STATUS_WEIGHTS = [
  [INSURANCE_STATUS.INTERESTED, 55],
  [INSURANCE_STATUS.CONVERTED, 25],
  [INSURANCE_STATUS.REJECTED, 20],
];

/** A filter value as strings, so ids compare cleanly whatever their type. */
function toStrings(value) {
  if (value === null || value === undefined || value === "") return null;
  return (Array.isArray(value) ? value : [value]).map(String);
}

/** Days between TODAY and a "YYYY-MM-DD" date. */
function daysSince(ymd) {
  const then = new Date(`${ymd}T00:00:00`);
  return Math.max(0, Math.round((TODAY.getTime() - then.getTime()) / 86400000));
}

const sellable = products.filter((p) => p.is_active);

export const leads = [];

for (let i = 0; i < LEAD_COUNT; i++) {
  // Every third person or so, spread across the list rather than the first 52
  // in a row, so the demo does not look like one contiguous slice of the file.
  const user = users[(i * 4 + 3) % users.length];
  // Active products only: the dropdown lists just those, so a lead against the
  // inactive one would be a row no product filter could ever reach.
  const product = sellable[int(0, sellable.length - 1)];
  const status = pickWeighted(STATUS_WEIGHTS);

  // The enquiry cannot predate the signup, so it is drawn from the window
  // between the two. That also keeps the two date filters independent: the
  // signup range and the enquiry range genuinely narrow different rows.
  const signupDaysAgo = daysSince(user.signed_up_date);
  const created = daysAgo(int(0, Math.min(90, signupDaysAgo)));
  const modified =
    status === INSURANCE_STATUS.INTERESTED
      ? created
      : new Date(Math.min(created.getTime() + int(1, 12) * 86400000, TODAY.getTime()));

  const id = 4100 + i;
  leads.push({
    id,
    first_name: user.first_name,
    last_name: user.last_name,
    mobile: user.mobile,
    email: user.email,
    status,
    utm_link: product.utm_link.replace("{LeadID}", String(id).padStart(5, "0")),
    created: stamp(created),
    modified: stamp(modified),
    /* Not shown in the table -- these are what the filters read. Both are
       local calendar dates, not UTC instants: the filter fields hand over
       "YYYY-MM-DD" as the user read it off a calendar, so comparing against a
       UTC instant would move a late-evening row into the next day. */
    product_id: product.id,
    product_name: product.title,
    signup_date: user.signed_up_date,
    created_date: ymd(created),
  });
}

/* -------------------------------------------------------------- queries */

/** Active products only, ordered by priority -- what the real list API does. */
export function insuranceProductList() {
  return products
    .filter((p) => p.is_active)
    .slice()
    .sort((a, b) => a.priority - b.priority);
}

/**
 * The panel list. Mirrors ProductHelper.get_webpanel_user_insurance_list plus
 * UserInsuranceProductList: filter, count the buckets, THEN apply the status
 * filter, then paginate.
 *
 * The bucket counts are deliberately taken before the status filter, exactly
 * as the server does it -- otherwise pressing one bucket would zero the other
 * three.
 */
export function insuranceList(params = {}) {
  const {
    status,
    search,
    insurance_product,
    from_date,
    to_date,
    signup_from_date,
    signup_to_date,
  } = params;

  let rows = leads.filter(
    (row) =>
      inRange(row.created_date, from_date, to_date) &&
      inRange(row.signup_date, signup_from_date, signup_to_date) &&
      matchesAny(String(row.product_id), toStrings(insurance_product)) &&
      textMatch(
        [row.first_name, row.last_name, `${row.first_name} ${row.last_name}`, row.email, row.mobile],
        search
      )
  );

  const countOf = (s) => rows.filter((row) => row.status === s).length;
  const counts = [
    { title: "All", count: rows.length, param: "all" },
    { title: INSURANCE_STATUS.INTERESTED, count: countOf(INSURANCE_STATUS.INTERESTED), param: INSURANCE_STATUS.INTERESTED },
    { title: INSURANCE_STATUS.CONVERTED, count: countOf(INSURANCE_STATUS.CONVERTED), param: INSURANCE_STATUS.CONVERTED },
    { title: INSURANCE_STATUS.REJECTED, count: countOf(INSURANCE_STATUS.REJECTED), param: INSURANCE_STATUS.REJECTED },
  ];

  // An array here means an admin locked the status for this user; a plain
  // string is the bucket strip. "all" is the strip's unfiltered state.
  if (Array.isArray(status)) {
    rows = rows.filter((row) => matchesAny(row.status, status));
  } else if (status && status !== "all") {
    rows = rows.filter((row) => row.status === status);
  }

  // Newest first, matching the server's order_by("-created"). The timestamps
  // are zero-padded "YYYY-MM-DD HH:MM", so a plain string compare is a date
  // compare.
  rows = rows.slice().sort((a, b) => (a.created < b.created ? 1 : -1));

  // Django's Paginator answers an out-of-range page with the LAST page rather
  // than an empty list -- UserInsuranceProductList.list catches EmptyPage and
  // does exactly that. Reproduced here because the filter bar does not reset
  // the page number: narrowing a search while on page 3 would otherwise land
  // past the end of the results and render a table with nothing in it.
  const size = Number(params.page_length) || PAGE_LENGTH;
  const lastPage = Math.max(1, Math.ceil(rows.length / size));
  const page = paginate(rows, {
    ...params,
    page_number: Math.min(Math.max(1, Number(params.page_number) || 1), lastPage),
    page_length: size,
  });
  return {
    results: page.results,
    counts,
    total_count: page.total_count,
    total_page_count: page.total_page_count,
    page_number: page.page_number,
  };
}

/**
 * Mark converted / rejected. In memory only, so it resets on reload -- the
 * same bargain every other mock-backed screen in this panel makes.
 */
export function setInsuranceStatus(id, status) {
  const row = leads.find((r) => String(r.id) === String(id));
  if (!row) return { updated: false };
  row.status = status;
  row.modified = stamp(new Date(TODAY.getTime()));
  return { updated: true, id: row.id, status };
}

export default { insuranceList, insuranceProductList, setInsuranceStatus };
